import { Component } from 'react';
import type { ReactNode } from 'react';

interface Props {
  /** When this value changes, the boundary clears any caught error and retries. */
  resetKey: string;
  children: ReactNode;
}

interface State {
  hasError: boolean;
  /** The resetKey the current error state is tied to. */
  resetKey: string;
}

/**
 * Lightweight error boundary for the live streaming preview.
 *
 * A pruned-but-incomplete spec can momentarily make the renderer throw; the
 * next `tool-input-delta` usually fixes it. Unlike the app-wide ErrorBoundary
 * this renders nothing on error (the preview is ephemeral), does NOT report to
 * Sentry (these throws are expected mid-stream), and auto-recovers whenever
 * `resetKey` changes — so a later, more-complete partial gets another chance.
 */
export class StreamingPreviewBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, resetKey: props.resetKey };
  }

  static getDerivedStateFromError(): Partial<State> {
    return { hasError: true };
  }

  static getDerivedStateFromProps(
    props: Props,
    state: State,
  ): Partial<State> | null {
    // A new partial spec arrived — clear the previous failure and retry it.
    if (props.resetKey !== state.resetKey) {
      return { hasError: false, resetKey: props.resetKey };
    }
    return null;
  }

  render(): ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}
